import { useEffect } from 'react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { ApiError } from '../../services/api/client'
import { canRetainReadData } from '../../services/api/read-revalidation'
import type {
  InteractionAppointmentContext,
  InteractionContextRepository,
  InteractionInsuranceContext,
  InteractionPatientContext,
  InteractionSummary,
} from './interaction-context-repository'

export type ContextState<T> =
  | { status: 'idle'; value: null }
  | { status: 'loading'; value: null }
  | { status: 'error'; value: null }
  | { status: 'ready'; value: T }

// Four independent resources retain their identities and failure boundaries.
// No legacy-route, SMS ownership or booking permission is cached here.
function useContextResource<T extends { aggregateId: string }>(
  read: ((tenantId: string, aggregateId: string) => Promise<T>) | undefined,
  tenantId: string,
  aggregateId: string | null,
  resource: string,
  repositoryPresent: boolean
): ContextState<T> & { refreshFailed?: boolean } {
  const query = useQuery({
    queryKey: ['interaction-context', tenantId, aggregateId, resource],
    queryFn: async () => {
      const value = await read!(tenantId, aggregateId!)
      if (value.aggregateId !== aggregateId) {
        throw new ApiError(
          'invalid-response',
          'Interaction identity did not match'
        )
      }
      return value
    },
    enabled: Boolean(read && aggregateId),
    staleTime: 30_000,
    // The session-scoped invalidation hub refreshes active context. This slow
    // fallback also covers canonical evidence without a conversation event.
    refetchInterval: 30_000,
  })
  if (!aggregateId || !repositoryPresent) return { status: 'idle', value: null }
  if (!read) return { status: 'error', value: null }
  if (query.data && (!query.isError || canRetainReadData(query.error))) {
    return { status: 'ready', value: query.data, refreshFailed: query.isError }
  }
  return { status: query.isError ? 'error' : 'loading', value: null }
}

export function useInteractionContext(
  repository: InteractionContextRepository | undefined,
  tenantId: string,
  aggregateId: string | null
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!aggregateId || !repository?.subscribe) return
    return repository.subscribe(tenantId, () => {
      void queryClient.invalidateQueries({
        queryKey: ['interaction-context', tenantId, aggregateId],
      })
    })
  }, [tenantId, aggregateId, repository, queryClient])
  return {
    patient: useContextResource<InteractionPatientContext>(
      repository?.getPatientContext,
      tenantId,
      aggregateId,
      'patient',
      Boolean(repository)
    ),
    appointment: useContextResource<InteractionAppointmentContext>(
      repository?.getAppointmentContext,
      tenantId,
      aggregateId,
      'appointment',
      Boolean(repository)
    ),
    insurance: useContextResource<InteractionInsuranceContext>(
      repository?.getInsuranceContext,
      tenantId,
      aggregateId,
      'insurance',
      Boolean(repository)
    ),
    summary: useContextResource<InteractionSummary>(
      repository?.getSummary,
      tenantId,
      aggregateId,
      'summary',
      Boolean(repository)
    ),
  }
}
